import { Controller } from "@hotwired/stimulus"

// Plan + interval picker for the watchlist onboarding step (no bounce to /pricing).
// The plan radios submit natively (name="plan"); this controller swaps the per-plan
// price on the monthly/annual toggle, keeps the hidden `interval` in sync, and
// reconciles the buyer's picks (each checked/typed competitor, one company apiece —
// the own company is a FREE, non-counting slot) against the plan's company cap.
// Two directions:
//
//   • Adding picks (auto mode) AUTO-BUMPS the plan UP to the smallest tier that
//     fits — otherwise picks beyond the cap are silently dropped post-payment by
//     MaterializeWatchlistJob.
//   • Clicking a plan SMALLER than the picks greys (disables) the lowest-priority
//     discovered competitors down to the cap — but keeps them CHECKED, so picking a
//     bigger plan again re-includes them with no re-selection. A disabled checkbox
//     doesn't submit, so the active (un-greyed) set is exactly what gets tracked.
export default class extends Controller {
  static targets = ["price", "interval", "tab", "annualNote", "plan", "capNote"]

  connect() {
    this.cappedCap = null // company cap pinned by a manual downgrade, or null (auto mode)
    this.bumped = false
    this.form = this.element.closest("form")
    this.onChange = this.handleChange.bind(this)
    this.form.addEventListener("change", this.onChange)
    // Custom competitors are added/removed by the watchlist-add controller, which
    // mutates the DOM rather than firing input events — watch its chip list. Scoped
    // to that list (not the whole form) so our own capNote writes can't re-trigger it.
    const list = this.form.querySelector('[data-watchlist-add-target="list"]')
    if (list) {
      this.observer = new MutationObserver(() => this.handlePickChange())
      this.observer.observe(list, { childList: true })
    }

    this.apply(this.intervalTarget.value)
    // Pre-checked recommendations are already picks — fit the plan to them on load.
    this.handlePickChange()
  }

  disconnect() {
    this.form?.removeEventListener("change", this.onChange)
    this.observer?.disconnect()
  }

  handleChange(event) {
    if (event.target.name === "interval") return
    event.target.name === "plan" ? this.selectPlan(event.target) : this.handlePickChange()
  }

  // A competitor / own-company toggle or a custom add/remove.
  handlePickChange() {
    if (this.cappedCap == null) {
      this.recountAndBump()
    } else {
      this.applyCap(this.cappedCap) // re-grey for the pinned plan
      this.updateCapNote()
    }
  }

  // The buyer explicitly picked a plan. Too small for the picks → pin it (capped
  // mode) and grey the excess; big enough → hand control back to auto-bump.
  selectPlan(radio) {
    this.bumped = false
    const cap = this.capOf(radio)
    this.cappedCap = cap < this.selectedCount() ? cap : null
    this.applyCap(this.cappedCap)
    this.updateCapNote()
  }

  // Auto mode only: raise the plan to the smallest tier that fits all the picks,
  // then grey anything still over the (top) plan's cap. Bumps UP only.
  recountAndBump() {
    const count = this.selectedCount()
    const current = this.selectedPlan()
    if (current && this.capOf(current) < count) {
      const fit = this.smallestFitting(count)
      if (fit && fit !== current) {
        fit.checked = true
        this.bumped = true
      }
    }
    const cap = this.capOf(this.selectedPlan())
    this.applyCap(cap < this.selectedCount() ? cap : null)
    this.updateCapNote()
  }

  // Grey (disable, but leave checked) the lowest-priority discovered competitors so
  // the active set fits `cap`; hand-typed picks are always kept, and the own company
  // (free, non-counting) is never greyed. A null cap re-enables everything. Disabled
  // checkboxes don't submit, so the active set equals what gets tracked, and the
  // greyed ones snap back on upgrade.
  applyCap(cap) {
    if (cap == null) {
      this.discoveredCheckboxes().forEach((cb) => this.setDiscoveredEnabled(cb, true))
      return
    }
    let slots = Math.max(0, cap - this.baseCount()) // discovered slots left after the typed customs
    this.discoveredCheckboxes().forEach((cb) => {
      if (!cb.checked) return this.setDiscoveredEnabled(cb, true) // selectable: queues as greyed if over cap
      const keep = slots > 0
      if (keep) slots -= 1
      this.setDiscoveredEnabled(cb, keep)
    })
  }

  setDiscoveredEnabled(checkbox, enabled) {
    checkbox.disabled = !enabled
    const label = checkbox.closest("label")
    label?.classList.toggle("opacity-50", !enabled)
    label?.classList.toggle("cursor-not-allowed", !enabled)
  }

  updateCapNote() {
    if (!this.hasCapNoteTarget) return
    const note = this.capNoteTarget
    const plan = this.selectedPlan()
    if (!plan) return note.classList.add("hidden")

    const selected = this.selectedCount()
    const cap = this.capOf(plan)
    const name = this.nameOf(plan)
    const show = (text, tone) => {
      note.textContent = text
      note.className = `text-center text-xs mt-2.5 text-${tone}-600`
    }

    if (selected <= cap) {
      if (this.bumped) show(`Switched to ${name} to fit your ${selected} ${this.companies(selected)}.`, "slate")
      else note.classList.add("hidden")
      return
    }

    const held = selected - cap
    if (this.baseCount() > cap) {
      // Typed competitors alone overflow — can't grey our way to fit.
      show(`${name} tracks ${cap} ${this.companies(cap)} — remove ${held} to fit.`, "amber")
    } else if (this.smallestFitting(selected)) {
      // The common downgrade: extras are kept, just inactive on this plan.
      show(`${name} tracks ${cap} — your other ${held} ${this.companies(held)} come back if you upgrade.`, "slate")
    } else {
      // Already on the top plan; there's no higher tier to restore them on.
      show(`${name} tracks ${cap} of your ${selected} — the rest won't be tracked.`, "amber")
    }
  }

  // Total companies the buyer has selected (intent) — INCLUDING ones greyed out by a
  // downgrade, which stay checked so an upgrade restores them.
  selectedCount() {
    return this.baseCount() + this.form.querySelectorAll('input[name="competitor_domains[]"]:checked').length
  }

  // Always-active companies that count toward the cap and never get greyed: every
  // custom (typed) pick. The own company is a FREE, non-counting slot, so it's
  // excluded from the cap math entirely (it's always tracked, never billed).
  baseCount() {
    return [...this.form.querySelectorAll('input[name="custom_competitors[]"]')].filter((i) => i.value.trim()).length
  }

  discoveredCheckboxes() {
    return [...this.form.querySelectorAll('input[name="competitor_domains[]"]')]
  }

  selectedPlan() {
    return this.planTargets.find((r) => r.checked)
  }

  // The smallest plan whose cap covers `count`; null when none can (caps null =
  // unlimited always fit). Relies on planTargets being rendered cheapest-first.
  smallestFitting(count) {
    return this.planTargets.find((r) => this.capOf(r) >= count) || null
  }

  capOf(radio) {
    const cap = parseInt(radio.dataset.cap, 10)
    return Number.isNaN(cap) ? Infinity : cap
  }

  nameOf(radio) {
    return radio.closest("label").querySelector(".font-semibold").textContent.trim()
  }

  companies(n) {
    return n === 1 ? "company" : "companies"
  }

  apply(interval) {
    this.intervalTarget.value = interval

    this.priceTargets.forEach((el) => {
      el.textContent = `$${interval === "year" ? el.dataset.annual : el.dataset.month}`
    })

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.interval === interval
      tab.classList.toggle("bg-white", active)
      tab.classList.toggle("text-cb-navy-900", active)
      tab.classList.toggle("shadow-sm", active)
      tab.classList.toggle("text-slate-500", !active)
    })

    this.annualNoteTargets.forEach((note) => note.classList.toggle("hidden", interval !== "year"))
  }

  selectInterval(event) {
    this.apply(event.params.interval)
  }
}
